import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { switchMap, take } from 'rxjs/operators';
import { PostTracker, PutTracker, Tracker } from 'src/app/interfaces/api/tracker';
import { TrackerConfig } from 'src/app/interfaces/api/trackerConfig';
import { TrackerService } from '../../services/api/trackers/tracker.service';

// Deep copy of a settings object - the config form edits custom fields' `value` in place, which
// must never leak back into the template object it came from.
export function cloneSettings<T>(value: T): T {
  return value === undefined ? value : JSON.parse(JSON.stringify(value));
}

// Shared by resources.component.ts (Ressources > Dispositif, all device types), kyd.component.ts/
// kyd-config-dialog.component.ts (Rapport KYD) and kyd-bulk-config-dialog.component.ts -
// configuring a device writes its values straight into the tracker's own `settings`, and links
// the template it was prefilled from (if any) through the tracker's `config` field. A field with
// no value on the tracker falls back to the linked template's own default (see
// initialSettings()). Saving as a reusable template is a deliberately separate action (see
// saveAsTemplate()/hasDiverged()).
//
// TrackerConfig.settings/the flat draft edited by TrackerConfigFormComponent is in meters for
// KYD; Tracker.settings (nested, also read/written by the mobile app - see
// kyd-connection-sync.service.ts) is in centimeters. Every read/write across that boundary
// converts with *100//100. Every other device type has no such unit split - its flat settings
// are already the exact shape stored on both TrackerConfig.settings and Tracker.settings, so the
// mapping is a straight passthrough.
@Injectable({
  providedIn: 'root'
})
export class TrackerConfigTool {
  constructor(
    private trackerService: TrackerService,
  ) { }

  // tracker.connecting/connectingAt are set/cleared by the mobile app around a Bluetooth
  // connection attempt (see unifield-mobile-2's ble-detail.page.ts pushConnectingStatus()) so
  // this site can show a live status and block editing while one is in flight, avoiding a
  // config save racing a device that's about to report its own real state right after
  // connecting. Treated as expired past this window regardless of the flag's own value, so a
  // phone that crashed or lost connectivity mid-attempt (and so never sent the closing
  // `connecting: false`) can't lock a tracker's configuration out forever.
  private static readonly CONNECTING_STALE_MS = 60000;

  isConnecting(tracker: Tracker | null | undefined): boolean {
    if (!tracker?.connecting || !tracker.connectingAt) return false;
    const age = Date.now() - new Date(tracker.connectingAt).getTime();
    return age >= 0 && age < TrackerConfigTool.CONNECTING_STALE_MS;
  }

  // tracker.config is either the populated TrackerConfig or its bare id depending on the
  // endpoint - this only ever returns the id (or undefined when nothing is linked).
  configIdOf(tracker: Tracker | null | undefined): string | undefined {
    const config: any = tracker?.config;
    const id = typeof config === 'string' ? config : (config?.id ?? config?._id);
    return id || undefined;
  }

  // The template linked to this tracker (tracker.config), if it still exists among `configs`
  // and matches the tracker's own type.
  linkedConfig(tracker: Tracker, configs: TrackerConfig[]): TrackerConfig | undefined {
    const id = this.configIdOf(tracker);
    const type = this.typeOf(tracker);
    return id ? configs.find(c => c.id === id && c.type === type) : undefined;
  }

  typeOf(tracker: Tracker): string {
    return tracker.type ?? tracker.settings?.type ?? 'Unit';
  }

  // The form's starting values when opening a tracker's own settings: every value already on
  // tracker.settings, and for anything missing there, the linked template's own default
  // (`config`, see linkedConfig()). For KYD with neither, sensible built-in defaults. Always a
  // deep copy - the form edits custom fields' `value` in place, which must never leak back into
  // the template object it came from.
  initialSettings(tracker: Tracker, config?: TrackerConfig): any {
    const type = this.typeOf(tracker);
    const defaults = cloneSettings(config?.settings ?? {});

    if (type === 'KYD') {
      const kydDefaults = Object.keys(defaults).length ? defaults : this.mapNestedToFlat(null, 'KYD');
      if (!tracker.settings?.distanceSettings && !tracker.settings?.friendSettings) return kydDefaults;

      const flat = this.mapNestedToFlat(tracker.settings, 'KYD');
      Object.keys(flat).forEach(key => {
        if (flat[key] == null && kydDefaults[key] != null) flat[key] = kydDefaults[key];
      });
      return flat;
    }

    const flat = defaults;
    Object.entries(tracker.settings ?? {}).forEach(([key, value]) => {
      const fallback = flat[key];
      // A raw value on the tracker (e.g. `ntrip: 4220`) for a field the template describes as a
      // custom field ({label, name, type, value}) keeps the template's field description, only
      // overriding its value.
      flat[key] = this.isCustomField(fallback) && !this.isCustomField(value)
        ? { ...fallback, value }
        : cloneSettings(value);
    });
    return flat;
  }

  private isCustomField(value: any): boolean {
    return value != null && typeof value === 'object' && 'value' in value;
  }

  mapNestedToFlat(nested: any, type: string | undefined = 'KYD'): any {
    if (type !== 'KYD') return nested ?? {};

    if (!nested) return { userMode: 'advanced', minDistance: 5, maxDistance: 15, friendGroup: 1, ignoreGroup: 0, unfriendDistance: 20, exclusionDistance: 1 };

    return {
      userMode: nested.userMode ?? 'advanced',
      minDistance: nested.distanceSettings?.minDistance != null ? nested.distanceSettings.minDistance / 100 : null,
      maxDistance: nested.distanceSettings?.maxDistance != null ? nested.distanceSettings.maxDistance / 100 : null,
      friendGroup: nested.friendSettings?.friendGroupNumber ?? 1,
      ignoreGroup: nested.friendSettings?.ignoreGroupNumber ?? 0,
      unfriendDistance: nested.friendSettings?.unfriendDistance != null ? nested.friendSettings.unfriendDistance / 100 : null,
      exclusionDistance: nested.friendSettings?.exclusionDistance != null ? nested.friendSettings.exclusionDistance / 100 : null,
    };
  }

  // `existingNested` is spread first so sections this form never exposes (e.g. alarmSettings -
  // LED/buzzer/alertProfile) are preserved untouched rather than dropped. Only meaningful for
  // KYD. Every other type only stores each custom field's raw value - its label/name/type
  // already live on the template (see initialSettings(), which merges them back for the form).
  mapFlatToNested(flat: any, type: string, existingNested: any = {}): any {
    if (type !== 'KYD') {
      const values = Object.fromEntries(Object.entries(flat ?? {}).map(([key, value]) => [key, this.isCustomField(value) ? (value as any).value : value]));
      return { ...existingNested, ...values, type };
    }

    return {
      ...existingNested,
      type: 'KYD',
      userMode: flat.userMode,
      distanceSettings: {
        ...(existingNested?.distanceSettings ?? {}),
        minDistance: flat.minDistance != null ? Math.round(flat.minDistance * 100) : null,
        maxDistance: flat.maxDistance != null ? Math.round(flat.maxDistance * 100) : null,
      },
      friendSettings: {
        ...(existingNested?.friendSettings ?? {}),
        friendGroupNumber: flat.friendGroup,
        ignoreGroupNumber: flat.ignoreGroup,
        unfriendDistance: flat.unfriendDistance != null ? Math.round(flat.unfriendDistance * 100) : null,
        exclusionDistance: flat.exclusionDistance != null ? Math.round(flat.exclusionDistance * 100) : null,
      },
    };
  }

  // Writes flat settings into the tracker's own `settings` - the ONLY thing "configuring a
  // device" does, whether the values were typed by hand or prefilled from an existing template -
  // a minimal PUT, deliberately NOT touching anything else on the tracker (name/version/
  // comment/...). `configId` links the template these values came from through `config`; omit it
  // to leave the current link untouched (the backend ignores an empty `config` anyway).
  // A KYD's settingsConfirmed is reset, since these values haven't been written to the sensor
  // over BLE yet.
  saveSettings(tracker: Tracker, flat: any, offlineDelay?: number, configId?: string | null): Observable<any> {
    const type = this.typeOf(tracker);
    const settings = this.mapFlatToNested(flat, type, tracker.settings);
    const payload: PutTracker = { id: tracker.id, uuid: tracker.uuid, settings };
    if (configId) payload.config = configId;
    if (offlineDelay !== undefined) payload.offlineDelay = offlineDelay;
    if (type === 'KYD') payload.settingsConfirmed = false;
    return this.trackerService.setTracker(payload);
  }

  // "Add a device" flows (trackers-modal.component.ts, edit-tracker-modal.component.ts) that
  // let the user configure it right away, at creation time - a Tracker has no `id`/`uuid` yet
  // for saveSettings()'s PUT to target, so this necessarily runs as two calls: create it bare
  // first, then save the configured values on the tracker that comes back, exactly as if it had
  // been configured a moment later through the normal per-device editor.
  createTrackerWithSettings(tracker: PostTracker, flat: any, offlineDelay?: number, configId?: string | null): Observable<any> {
    return this.trackerService.addTracker(tracker).pipe(
      switchMap((created: Tracker) => this.saveSettings({ ...created, type: created.type ?? tracker.type }, flat, offlineDelay, configId))
    );
  }

  // Creates or updates a reusable TrackerConfig template - the ONLY way one gets created from
  // these per-device editors (see "Enregistrer comme modèle"). Same endpoint/upsert-by-id
  // behavior as tracker-configs.component.ts's own global template manager (Devices > engrenage).
  saveAsTemplate(draft: TrackerConfig): Observable<any> {
    return this.trackerService.setConfig(draft as any);
  }

  // Fresh templates list, once - getConfigs() alone stays open on the live socket.
  fetchConfigs(): Observable<TrackerConfig[]> {
    return this.trackerService.getConfigs().pipe(take(1));
  }

  // Whether the current draft still matches the template it was prefilled from - drives the
  // "update the template too?" prompts on both save paths. offlineDelay lives outside
  // `settings` so it's compared separately.
  hasDiverged(flatA: any, offlineDelayA: number | undefined, flatB: any, offlineDelayB: number | undefined): boolean {
    return JSON.stringify(flatA ?? {}) !== JSON.stringify(flatB ?? {}) || (offlineDelayA ?? null) !== (offlineDelayB ?? null);
  }
}
